import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { NodeData, type PickedReference, type ReferenceTag } from '../../types';
import type { ReferenceChoice, ReferenceChoiceKind } from '../../utils/workspaceEdit';
import { isPickedInputId } from '../../utils/pickedReferences';
import { useAddReferences, type ReferenceSharing } from './AddReferencesButton';
import { InputTile, ModeSwitch, SendGroup, SettingChip, SettingsRow, TileRemoveButton, promptFieldClass, type ChipOption } from './composer/Composer';
import { PromptTagLayer, PromptTagPicker, REF_TAG, REF_TAG_IDLE, usePromptTags, type PromptTagRef } from './composer/PromptTags';
import { PROVIDER_NAMES, shortModelLabel } from './composer/modelLabels';
import { GlassOrb } from '../GlassOrb';
import { AI_MODELS, getVideoModels, calculateCost, getVeniceVideoModelId } from '../../models/config';
import { videoSettingsOf } from '../../utils/generatorSettings';
import { Video, Loader2, Play, AlertCircle, ChevronRight, Download, Link as LinkIcon, X, DollarSign, Clock, Twitter, Type, Image as ImageIcon, Film, Layers, Plus, Users, Music, RectangleHorizontal, Clock3, Volume2, VolumeX, Scan, ImagePlus, AudioLines } from 'lucide-react';
import {
  veniceQueueVideo, veniceRetrieveVideo, veniceVideoQuote, veniceProgressPercent,
  assertSeedanceReferenceImage, VeniceError, VeniceSeedanceConsents,
} from '../../utils/venice';
import { ShareToTwitterModal } from '../ShareToTwitterModal';
import { GoogleGenAI, VideoGenerationReferenceType } from "@google/genai";
import { useFileUrl } from '../../hooks/useFileUrl';
import { useAutoGrowTextarea } from '../../hooks/useAutoGrowTextarea';
import { useFitNodeHeight } from '../../hooks/useFitNodeHeight';
import { checkAudioForVenice, formatDuration } from '../../utils/audioReference';
import { imageTag, normalizeReferenceTags, toSeedanceTags } from '../../utils/promptMentions';
import { videoModeOf, videoPickKinds, videoReferenceTags, videoTakesAudio, videoTakesClips } from '../../utils/nodeReferenceTags';
import { checkKlingReferences, klingReferenceFields } from '../../utils/klingReferences';
import { uploadWithDeduplication, markBlobUploading, finishBlobUpload, isBlobUploading, resolveUploadedBlob, isRehydratedBlob, rememberSessionBlob } from '../../utils/fileRegistry';
import { FileImage } from '../FileImage';
import { loadMediaAsBase64, loadMediaBlob } from '../../utils/mediaLoader';
import { CLIP_MIN_SEC, DEFAULT_REFERENCE_LIMITS, checkReferenceClips, defaultClipRange, type ClipRange } from '../../utils/referenceClips';
import { getTrimmedClip, probeVideoDuration } from '../../utils/videoTrim';
import { clipBitrateBudget, fitPicturesToRequest, oversizedRequest, referenceStep, requestBytes } from '../../utils/veniceRequestSize';
import { ReferenceClipEditor } from './ReferenceClipEditor';
import { autoSaveFile, isAutoSaveReady, registerLocalFileMapping } from '../../utils/localSave';
import { VideoPlayer } from '../VideoPlayer';
import { VeniceBalanceLabel } from '../VeniceBalance';
import { refreshVeniceBalance } from '../../hooks/useVeniceBalance';

interface VideoGeneratorNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  /** Pictures connected or picked; a Reference node's tag tells Kling's characters from its scenes. */
  inputImages?: { id: string, url: string, label?: string, tag?: ReferenceTag }[];
  /** Audio nodes connected to this node (Seedance reference mode: background music or voice/SFX donors). */
  inputAudio?: { id: string; url: string; label: string; durationSec?: number; sizeBytes?: number }[];
  /** Video nodes connected to this node (Seedance reference mode: clips to continue or restyle). */
  inputVideos?: { id: string; sourceId: string; url: string; label: string }[];
  onRemoveConnection?: (connectionId: string) => void;
  apiKey?: string;
  openAiApiKey?: string;
  veniceApiKey?: string;
  onRequestApiKey?: (provider: 'google' | 'openai' | 'venice') => void;
  userId?: string; // For resolving file:// references
  workspaceName?: string; // For storage paths
  nodeId?: string; // For file registry
  /** Current node box; the node grows with the prompt field while typing, and when its options would not fit. */
  size?: { width: number; height: number };
  onResize?: (id: string, newSize: { width: number; height: number }) => void;
  onAutoSaveStatus?: (type: 'local' | 'cloud' | 'both', message: string) => void;
  /** Merge a partial update into the node's live data; the guard sees current data and can veto a late result. Returns whether it applied. */
  onDataPatch?: (id: string, patch: Partial<NodeData>, guard?: (current: NodeData) => boolean) => boolean;
  onAutoSaveFile?: (fileData: string | Blob, fileName: string, category?: 'Generated_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads', fileExtension?: string) => Promise<void>;
  isViewOnly?: boolean;
  ownerUserId?: string;
  /** The workspace's media, read when the reference list opens. */
  getReferenceChoices?: () => ReferenceChoice[];
  /** Stores the references chosen from that list on the node. */
  onPickedReferencesChange?: (next: PickedReference[]) => void;
  /** In a project: the list also offers the Reference nodes of its other workspaces. */
  referenceSharing?: ReferenceSharing;
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the clip'));
    reader.readAsDataURL(blob);
  });

/** The generation modes as the switch shows them. */
const MODE_INFO: Record<string, { label: string; icon: React.ReactNode }> = {
  'text-to-video': { label: 'Text', icon: <Type size={14} /> },
  'image-to-video': { label: 'Image', icon: <ImageIcon size={14} /> },
  'frames-to-video': { label: 'Frames', icon: <Film size={14} /> },
  'ingredients-to-video': { label: 'Ingredients', icon: <Layers size={14} /> },
  'extend-video': { label: 'Extend', icon: <Plus size={14} /> },
  'reference-to-video': { label: 'Reference', icon: <Users size={14} /> },
};


/**
 * A picture used as a reference, small: the same tile wherever the node lists them, and the
 * same as in the Image node (60px, corner 12). At that width the tag under it (@Image1) is
 * readable in full.
 */
const REF_THUMB = 'w-[60px] h-[60px] rounded-lg overflow-hidden bg-cream-200 ring-1 ring-cream-300 shrink-0';

/**
 * Something the model will not take as it is (too many references, a clip that is too long, a
 * prompt over the limit, a tag that names nothing): said the same way wherever it comes up in
 * the node, as the design system's note in the colours of a warning.
 */
const Problem: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, className = '', ...rest }) => (
  <p className={`ns-note ${className}`} data-tone="warn" {...rest}>
    <AlertCircle aria-hidden="true" />
    <span className="min-w-0">{children}</span>
  </p>
);

const SEEDANCE_CONSENTS: { seedance: VeniceSeedanceConsents } = {
  seedance: {
    confirmed_terms_and_privacy: true,
    confirmed_legal_right: true,
    confirmed_screening_acknowledged: true,
  },
};

export const VideoGeneratorNode: React.FC<VideoGeneratorNodeProps> = ({
  data,
  onDataChange,
  inputImages = [],
  inputAudio = [],
  inputVideos = [],
  onRemoveConnection,
  apiKey,
  openAiApiKey,
  veniceApiKey,
  onRequestApiKey,
  userId,
  workspaceName = 'Untitled',
  nodeId,
  size,
  onResize,
  onAutoSaveStatus,
  onAutoSaveFile,
  onDataPatch,
  isViewOnly = false,
  ownerUserId,
  getReferenceChoices,
  onPickedReferencesChange,
  referenceSharing,
}) => {
  // A video generated in this session plays from its local copy while the cloud upload
  // runs and after the node adopts the file:// reference: no lookup, no restart. The
  // copy belongs to a job, so a new generation replaces it.
  const [sessionVideo, setSessionVideo] = useState<{ jobId?: string; url: string } | null>(null);
  const hasSessionCopy = !!sessionVideo && !!data.jobId && sessionVideo.jobId === data.jobId;
  // Resolve file:// reference to actual URL for video (local-first if workspace auto-save enabled)
  const { url: resolvedVideoUrl, isLoading: isResolvingVideo } = useFileUrl(userId, hasSessionCopy ? null : data.videoUri, workspaceName, ownerUserId);
  const videoSrc = hasSessionCopy && sessionVideo ? sessionVideo.url : (resolvedVideoUrl || data.videoUri);
  const [isGenerating, setIsGenerating] = useState(data.generationStatus === 'generating');
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);
  // Prompt field: auto-grows with its content (growing the node with it) and offers @Image tags
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const [missingKeyType, setMissingKeyType] = useState<'google' | 'openai' | 'venice'>('google');
  const [showTwitterModal, setShowTwitterModal] = useState(false);
  // A failed start while a previous job's upload is still pending is shown here, so the
  // node's saved state stays resumable instead of being demoted to 'error'.
  const [startError, setStartError] = useState<string | null>(null);
  // Shown in the generating overlay while reference clips are read and trimmed before the job is queued.
  const [prepMessage, setPrepMessage] = useState<string | null>(null);

  // Polling Interval Ref
  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track whether this component instance initiated a generation (vs stale state from page reload)
  const activeGenerationRef = useRef(false);

  // Ref-stable version of pollSoraStatus to avoid setInterval capturing stale closures
  const pollSoraStatusRef = useRef<(jobId: string) => Promise<void>>();

  // Venice (Seedance) job polling + UI state
  const pollVeniceStatusRef = useRef<(queueId: string, veniceModel: string) => Promise<void>>();
  const veniceErrorStreakRef = useRef(0);
  // /video/retrieve streams the whole mp4 on completion, which can outlast the poll
  // interval; this guard stops a second tick from re-downloading and double-persisting.
  const veniceInFlightRef = useRef<string | null>(null);
  // Queue id of the generation this node currently follows; ticks from an older one are ignored.
  const activeJobRef = useRef<string | null>(null);
  // Late results (a background upload) must not touch a node instance that has gone away.
  const isMountedRef = useRef(true);
  // Bumped by every Generate and by Cancel; a start request that finishes under a stale token is dropped.
  const runTokenRef = useRef(0);
  // Stops the clip preparation (download and trim) of the run under way, on Cancel
  const prepAbortRef = useRef<AbortController | null>(null);
  const [veniceConsent, setVeniceConsent] = useState<{ message: string; docsUrl?: string } | null>(null);
  const [veniceQuote, setVeniceQuote] = useState<number | null>(null);
  const [veniceQuoteLoading, setVeniceQuoteLoading] = useState(false);

  // Default values
  const prompt = data.prompt || '';
  
  const savedModelId = data.videoConfig?.model;
  const currentModelId = (savedModelId && AI_MODELS[savedModelId]) 
      ? savedModelId 
      : 'veo-3.1-fast-generate-preview';
  
  const modelDef = AI_MODELS[currentModelId] || AI_MODELS['veo-3.1-fast-generate-preview'];

  // Saved values are validated against the current model's option lists so a
  // value persisted for another provider (Veo "5" vs Seedance "5s", Veo "4k")
  // is never displayed as one thing and sent as another.
  // (utils/generatorSettings.ts, shared with the Claude connector)
  const { aspectRatio, resolution, duration, audio: audioEnabled, audioRole } = videoSettingsOf(data.videoConfig, modelDef);
  
  // Video generation mode (Veo specific)
  const availableModes = useMemo(() => {
      return (modelDef as any).supportedVideoModes || ['text-to-video'];
  }, [modelDef]);
  const generationMode = useMemo(() => videoModeOf(modelDef, data.videoConfig?.generationMode), [modelDef, data.videoConfig?.generationMode]);
  const negativePrompt = data.videoConfig?.negativePrompt || '';
  // (Seedance audio generation, audioEnabled above: Venice default is on)
  const isVenice = modelDef.provider === 'venice';
  // Exact Venice model id for the selected family + mode (e.g. seedance-2-5-image-to-video-basic)
  const veniceModelId = useMemo(
      () => (isVenice ? getVeniceVideoModelId(modelDef, generationMode) : undefined),
      [isVenice, modelDef, generationMode],
  );

  const status = data.generationStatus || 'idle';
  const cost = data.cost || 0;

  // Progress display
  const progressPercent = data.progress || 0;
  // Only the client that queued a job follows it. Jobs saved before the owner was recorded
  // are not auto-resumed for a signed-in user (fail closed), but that user may still cancel them.
  const ownsCurrentJob = data.videoConfig?.generationOwnerId ? data.videoConfig.generationOwnerId === userId : !userId;
  const mayControlJob = !data.videoConfig?.generationOwnerId || data.videoConfig.generationOwnerId === userId;
  // Controls lock while this instance generates, or while another client's job is running on the node.
  const busy = isGenerating || (status === 'generating' && !hasSessionCopy && !ownsCurrentJob);

  // Latest node data for async flows (polling, uploads), so an update that lands
  // minutes after it was scheduled merges onto current data instead of reverting it.
  const dataRef = useRef(data);
  dataRef.current = data;
  const updateData = useCallback((updates: Partial<NodeData>) => {
    if (isViewOnly) return;
    dataRef.current = { ...dataRef.current, ...updates };
    onDataChange(dataRef.current);
  }, [onDataChange, isViewOnly]);

  // ── Prompt field: it fills the room the options leave, and grows the node while typing; @Image tags ──
  const fitRef = useRef<HTMLDivElement>(null);
  useFitNodeHeight(fitRef, nodeId, size, onResize);
  const { markUserEdit: markPromptEdit } = useAutoGrowTextarea(promptRef, prompt, {
    max: 360,
    onUserGrow: (delta) => { if (nodeId && onResize && size) onResize(nodeId, { width: size.width, height: size.height + delta }); },
  });

  const maxRefs = modelDef.maxReferenceImages || 9;
  // A prompt written for a model that takes more (Seedance: up to 15,000) can be too long for this one
  const promptOverLimit = modelDef.promptCharacterLimit ? prompt.length - modelDef.promptCharacterLimit : 0;
  // Connected audio: only Seedance 2.x reference mode accepts it (as music or as voice/SFX donors; audioRole above)
  const audioInputSupported = videoTakesAudio(modelDef, generationMode);

  // Connected videos become reference clips (Seedance reference mode). The selected part of
  // each is remembered per source node; the clip's length is learned from its preview.
  const videoInputSupported = videoTakesClips(modelDef, generationMode);

  // What the prompt can name, reference by reference (utils/nodeReferenceTags.ts, shared with the
  // Claude connector). Kling O3's reference mode names pictures by kind, from their Reference tags:
  // characters and objects are elements (@Element1…), backgrounds and styles are scene pictures (@Image1…)
  const referenceTags = useMemo(
    () => videoReferenceTags(modelDef, generationMode, audioRole, inputImages, inputVideos.length, inputAudio.length),
    [modelDef, generationMode, audioRole, inputImages, inputVideos.length, inputAudio.length],
  );
  const klingReferenceSlots = referenceTags.klingSlots;
  /** The tag of the picture at this place in the strip. */
  const tagOf = useCallback((index: number) => klingReferenceSlots?.[index]?.tag ?? imageTag(index), [klingReferenceSlots]);
  const klingProblems = useMemo(() => (klingReferenceSlots ? checkKlingReferences(klingReferenceSlots) : []), [klingReferenceSlots]);
  const clipRanges: Record<string, ClipRange> = data.videoConfig?.referenceClips ?? {};
  // How many and how long: Seedance 2.5 takes clips and audio of up to 30 s, 2.0 up to 15 s
  const referenceLimits = modelDef.referenceMediaLimits ?? DEFAULT_REFERENCE_LIMITS;

  // References chosen from the workspace's media instead of connected: pictures in every mode
  // that takes images, and in Reference mode also clips and audio where the model takes them
  const pickKinds: ReferenceChoiceKind[] = videoPickKinds(modelDef, generationMode);
  const connectedUrls = [...inputImages, ...inputAudio, ...inputVideos].filter(input => !isPickedInputId(input.id)).map(input => input.url);
  // The list of Reference nodes, opened from an input tile for the kind it takes
  const refPicker = useAddReferences({
    kinds: pickKinds,
    picked: data.pickedReferences,
    connectedUrls,
    getChoices: getReferenceChoices,
    onChange: isViewOnly ? undefined : onPickedReferencesChange,
    userId,
    ownerUserId,
    workspaceName,
    sharing: referenceSharing,
  });
  const openPicker = refPicker.available && !busy ? (anchor: HTMLElement, kind: ReferenceChoiceKind) => refPicker.open(anchor, [kind]) : undefined;
  const extendFromClip = !!data.videoConfig?.extendFromClip;
  const matchClipAspect = data.videoConfig?.matchClipAspect !== false;
  const [clipDurations, setClipDurations] = useState<Record<string, number>>({});
  const clipProblems = useMemo(() => {
      if (!videoInputSupported || inputVideos.length === 0) return [];
      return checkReferenceClips(inputVideos.map((clip, i) => {
          const known = clipDurations[clip.sourceId];
          const range = clipRanges[clip.sourceId] ?? (known ? defaultClipRange(known, referenceLimits) : undefined);
          return { label: clip.label || `Clip ${i + 1}`, startSec: range?.startSec ?? 0, endSec: range?.endSec ?? (known ?? CLIP_MIN_SEC) };
      }), referenceLimits);
  }, [videoInputSupported, inputVideos, clipDurations, clipRanges, referenceLimits]);
  /** Merge fields into videoConfig without disturbing the option normalisation done by updateConfig. */
  const patchVideoConfig = (patch: Partial<NonNullable<NodeData['videoConfig']>>) => {
      updateData({
          videoConfig: {
              ...dataRef.current.videoConfig,
              model: currentModelId,
              aspectRatio,
              resolution,
              duration,
              generationMode,
              negativePrompt,
              audio: audioEnabled,
              audioRole,
              ...patch,
          },
      });
  };
  const audioProblems = useMemo(
    () => (audioInputSupported ? checkAudioForVenice(audioRole, inputAudio.map(a => ({ label: a.label, durationSec: a.durationSec, sizeBytes: a.sizeBytes })), inputImages.length + inputVideos.length, referenceLimits) : []),
    [audioInputSupported, audioRole, inputAudio, inputImages.length, inputVideos.length, referenceLimits],
  );
  // Everything the prompt can name, in the order it is sent: the pictures (@Image1…, for Kling
  // also @Element1…), then for Seedance the clips (@Video1…) and the audio (@Audio1…)
  const mentionRefs = useMemo(() => {
      if (!isVenice || generationMode !== 'reference-to-video') return [];
      const refs: PromptTagRef[] = [];
      inputImages.forEach((img, i) => { const tag = referenceTags.images[i]; if (tag) refs.push({ tag, label: img.label || `Reference ${i + 1}`, kind: 'image', url: img.url }); });
      inputVideos.forEach((clip, i) => { const tag = referenceTags.videos[i]; if (tag) refs.push({ tag, label: clip.label || `Clip ${i + 1}`, kind: 'video', url: clip.url }); });
      inputAudio.forEach((clip, i) => { const tag = referenceTags.audio[i]; if (tag) refs.push({ tag, label: clip.label || `Audio ${i + 1}`, kind: 'audio', url: clip.url }); });
      return refs;
  }, [isVenice, generationMode, inputImages, inputVideos, inputAudio, referenceTags]);
  const tags = usePromptTags({
    prompt,
    refs: mentionRefs,
    fieldRef: promptRef,
    onPrompt: value => { markPromptEdit(); updateData({ prompt: value }); },
    userId,
    workspaceName,
  });
  const mentionsEnabled = tags.enabled;
  const { insertTag, unknownTags } = tags;
  /** The tag for a clip or an audio file, when the prompt can name it. */
  const refTag = (kind: 'video' | 'audio', url: string) => mentionRefs.find(ref => ref.kind === kind && ref.url === url)?.tag;
  const insertTagFromStrip = (index: number) => insertTag(tagOf(index));

  const updateConfig = (key: string, value: string | boolean) => {
    // On a model switch, persist values that the new model actually supports.
    const nextModel = key === 'model' ? (AI_MODELS[String(value)] || modelDef) : modelDef;
    const kept = videoSettingsOf({ aspectRatio, resolution, duration }, nextModel);
    updateData({
      videoConfig: {
        ...dataRef.current.videoConfig, // keep job bookkeeping (queue id, download URL, owner)
        model: currentModelId,
        aspectRatio: kept.aspectRatio,
        resolution: kept.resolution,
        duration: kept.duration,
        generationMode,
        negativePrompt,
        audio: audioEnabled,
        audioRole,
        [key]: value,
      }
    });
  };

  // Live price estimate for Venice (billed by duration × resolution × audio); debounced.
  useEffect(() => {
      if (!isVenice || !veniceApiKey || !veniceModelId) {
          setVeniceQuote(null);
          setVeniceQuoteLoading(false);
          return;
      }
      let cancelled = false;
      setVeniceQuoteLoading(true);
      setVeniceQuote(null); // never show the previous configuration's price while re-quoting
      const t = setTimeout(async () => {
          try {
              const q = await veniceVideoQuote({
                  apiKey: veniceApiKey,
                  model: veniceModelId,
                  duration,
                  resolution: modelDef.supportedResolutions ? resolution : undefined,
                  aspectRatio: generationMode === 'image-to-video' ? undefined : aspectRatio,
                  audio: modelDef.supportsAudioToggle ? audioEnabled : undefined,
              });
              if (!cancelled) setVeniceQuote(q);
          } catch (e) {
              if (!cancelled) setVeniceQuote(null);
          } finally {
              if (!cancelled) setVeniceQuoteLoading(false);
          }
      }, 500);
      return () => { cancelled = true; clearTimeout(t); };
  }, [isVenice, veniceApiKey, veniceModelId, duration, resolution, aspectRatio, audioEnabled, generationMode, modelDef.supportsAudioToggle, modelDef.supportedResolutions]);

  const checkApiKey = async (): Promise<boolean> => {
    if (modelDef.provider === 'openai') {
        if (!openAiApiKey) {
            setMissingKeyType('openai');
            setShowApiKeyPrompt(true);
            return false;
        }
        return true;
    } else if (modelDef.provider === 'venice') {
        if (!veniceApiKey) {
            setMissingKeyType('venice');
            setShowApiKeyPrompt(true);
            return false;
        }
        return true;
    } else {
        if (apiKey) return true;
        const win = window as any;
        if (win.aistudio && win.aistudio.hasSelectedApiKey) {
           const hasKey = await win.aistudio.hasSelectedApiKey();
           if (!hasKey) {
             setMissingKeyType('google');
             setShowApiKeyPrompt(true);
             return false;
           }
        }
        return true;
    }
  };

  const handleSelectKey = async () => {
    if (onRequestApiKey) {
        onRequestApiKey(missingKeyType);
        setShowApiKeyPrompt(false);
        return;
    }
    if (missingKeyType === 'openai' || missingKeyType === 'venice') {
        alert(`Enter your ${missingKeyType === 'openai' ? 'OpenAI' : 'Venice AI'} key in Settings → API Keys`);
        setShowApiKeyPrompt(false);
        return;
    }
    const win = window as any;
    if (win.aistudio && win.aistudio.openSelectKey) {
      await win.aistudio.openSelectKey();
      setShowApiKeyPrompt(false);
    }
  };

  /** Bytes of a connected image or audio clip for the provider request; errors name the input. */
  const getBase64FromUrl = (url: string, label = 'the connected image', defaultMimeType = 'image/png') =>
      loadMediaAsBase64(url, { userId, ownerUserId, workspaceName, label, defaultMimeType });

  // Helper to resize image to exact dimensions required by Sora
  const resizeImageToBlob = async (base64Data: string, width: number, height: number): Promise<Blob> => {
      return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (!ctx) { reject(new Error("Canvas error")); return; }
              
              // Object-fit: cover logic
              const scale = Math.max(width / img.width, height / img.height);
              const x = (width - img.width * scale) / 2;
              const y = (height - img.height * scale) / 2;
              
              ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
              
              canvas.toBlob((blob) => {
                  if (blob) resolve(blob);
                  else reject(new Error("Blob error"));
              }, 'image/png');
          };
          img.onerror = () => reject(new Error("Failed to load image for resizing"));
          img.src = base64Data;
      });
  };

  // --- SORA JOB RECOVERY & POLLING ---
  
  const clearPolling = () => {
      if (pollInterval.current) { clearInterval(pollInterval.current); pollInterval.current = null; }
  };

  // Shared tail for async providers (Sora, Venice): save the mp4 locally, show it from
  // a session copy, and upload it in the background. The node's saved state stays
  // 'generating' (resumable from its job id) until the file reference is durable, so a
  // reload, a workspace switch or a collaborator's sync during the upload can never
  // strand the video. `extra` merges into the final node data.
  const persistVideoBlob = async (blob: Blob, jobId: string | undefined, extra: Partial<NodeData> = {}) => {
      const blobUrl = URL.createObjectURL(blob);

      // Convert blob to base64 for uploads and auto-save
      const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
      });

      // Generate shared timestamp for consistent filenames
      const timestamp = Date.now() + Math.floor(Math.random() * 1000);
      const filename = `videos_${timestamp}.mp4`;

      // AUTO-SAVE to the local folder if enabled
      try {
          if (onAutoSaveFile) {
              await onAutoSaveFile(base64, filename, 'Videos', 'mp4');
          }
      } catch (autoSaveError) {
          console.warn('Video auto-save failed:', autoSaveError);
      }

      // Show the video now; the Generate button comes back at the same time.
      activeGenerationRef.current = false;
      if (isMountedRef.current) {
          setSessionVideo({ jobId, url: blobUrl });
          setIsGenerating(false);
      }

      // Final data is adopted against the node's live state, and only while it still belongs to
      // this job: a node that moved on carries another job id or has already completed. Any
      // interim status (an error from a failed later start, a stale reference) is overwritten.
      const stillThisJob = (current: NodeData) => !!jobId && current.jobId === jobId && current.generationStatus !== 'completed';
      const adopt = (patch: Partial<NodeData>): boolean => {
          if (onDataPatch && nodeId) return onDataPatch(nodeId, patch, stillThisJob);
          if (isMountedRef.current && stillThisJob(dataRef.current)) {
              updateData(patch);
              return true;
          }
          return false;
      };
      const completion = (videoUri: string): Partial<NodeData> =>
          ({ videoUri, generationStatus: 'completed', timestamp: Date.now(), progress: 100, ...extra });

      if (!userId) {
          adopt(completion(blobUrl));
          return;
      }

      markBlobUploading(blobUrl);
      onAutoSaveStatus?.('cloud', 'Uploading video to cloud...');
      void uploadWithDeduplication(userId, base64, {
          category: 'Videos',
          workspaceName,
          displayNamePrefix: 'videos', // Match local filename format
          extension: 'mp4',
          nodeId,
          timestamp, // Use same timestamp as local save
      }).then((fileRef) => {
          if (!fileRef.startsWith('file://')) throw new Error('Upload did not return a file reference');
          finishBlobUpload(blobUrl, fileRef);
          registerLocalFileMapping(fileRef, `Videos/videos_${timestamp}.mp4`);
          if (adopt(completion(fileRef))) {
              onAutoSaveStatus?.('cloud', 'Uploaded video to cloud');
          } else {
              // The node moved on (workspace closed, new generation). Its saved 'generating'
              // state fetches the job again on reopen, and the content hash reuses this upload.
              console.info('[VideoGenerator] Upload finished after the node moved on; it is attached on reopen.');
          }
      }).catch((uploadError) => {
          finishBlobUpload(blobUrl);
          console.warn('Background video upload failed; the next workspace save will retry:', uploadError);
          onAutoSaveStatus?.('cloud', 'Video upload failed; it will retry on the next save');
          // Fall back to the local copy; the workspace save uploads a lingering blob itself.
          adopt(completion(blobUrl));
      });
  };

  const finalizeSoraVideo = async (jobId: string) => {
      console.log('[Sora] Finalizing video, downloading content for job:', jobId);
      // A Cancel or a new Generate during the download abandons this clip.
      const token = runTokenRef.current;
      const abandoned = () => token !== runTokenRef.current || dataRef.current.jobId !== jobId;
      try {
          const res = await fetch(`https://api.openai.com/v1/videos/${jobId}/content`, {
              headers: { 'Authorization': `Bearer ${openAiApiKey}` }
          });
          if (!res.ok) throw new Error("Failed to download video content");

          const blob = await res.blob();
          if (abandoned()) return;
          await persistVideoBlob(blob, jobId, { cost: modelDef.baseCost });
      } catch (e: any) {
          if (abandoned()) return;
          activeGenerationRef.current = false;
          updateData({ generationStatus: 'error', errorMessage: e.message });
          setIsGenerating(false);
      }
  };

  const pollSoraStatus = useCallback(async (jobId: string) => {
      if (!openAiApiKey) return;
      if (activeJobRef.current !== jobId) return; // cancelled, superseded, or a leaked interval
      
      console.log('[Sora] Polling status for job:', jobId);
      
      try {
          const res = await fetch(`https://api.openai.com/v1/videos/${jobId}`, {
              headers: { 'Authorization': `Bearer ${openAiApiKey}` }
          });
          
          if (!res.ok) {
              if (res.status === 404) throw new Error("Job not found (Expired?)");
              throw new Error("Status check failed");
          }

          const data = await res.json();
          if (activeJobRef.current !== jobId) return; // cancelled or superseded while the request was in flight
          
          console.log('[Sora] Job status:', data.status, 'progress:', data.progress);
          
          if (data.status === 'completed' || data.status === 'succeeded') {
              if (pollInterval.current) { clearInterval(pollInterval.current); pollInterval.current = null; }
              activeJobRef.current = null; // a tick from a leaked interval must not finalize again
              await finalizeSoraVideo(jobId);
          } else if (data.status === 'failed') {
               if (pollInterval.current) { clearInterval(pollInterval.current); pollInterval.current = null; }
               activeGenerationRef.current = false;
               const errorMsg = data.error?.message || data.failure_reason || "Generation Failed";
               updateData({ generationStatus: 'error', errorMessage: errorMsg });
               setIsGenerating(false);
          } else {
               // Update Progress (queued, in_progress)
               const progress = typeof data.progress === 'number' ? data.progress : 0;
               updateData({ progress });
          }
      } catch (e: any) {
          if (activeJobRef.current !== jobId) return; // a late failure for a job this node no longer follows
          if (pollInterval.current) { clearInterval(pollInterval.current); pollInterval.current = null; }
          activeGenerationRef.current = false;
          updateData({ generationStatus: 'error', errorMessage: e.message });
          setIsGenerating(false);
      }
  }, [openAiApiKey, updateData, modelDef.baseCost]);

  // Keep ref in sync with latest version
  useEffect(() => {
      pollSoraStatusRef.current = pollSoraStatus;
  }, [pollSoraStatus]);

  // ── Venice (Seedance) ──────────────────────────────────────────────────────
  // POST /video/retrieve answers JSON while PROCESSING and streams video/mp4 when done.
  const pollVeniceStatus = useCallback(async (queueId: string, veniceModel: string) => {
      if (!veniceApiKey) return;
      if (activeJobRef.current !== queueId) return; // a newer generation owns this node
      if (veniceInFlightRef.current) return; // previous tick still downloading
      veniceInFlightRef.current = queueId;
      try {
          let lastReported = -1;
          const result = await veniceRetrieveVideo({
              apiKey: veniceApiKey,
              model: veniceModel,
              queueId,
              downloadUrl: dataRef.current.videoConfig?.veniceDownloadUrl,
              // The finished file streams back in this call; move the bar past the 95% cap while it arrives.
              onProgress: (received, total) => {
                  if (!total || activeJobRef.current !== queueId) return;
                  const percent = Math.min(99, 95 + Math.round((received / total) * 4));
                  if (percent !== lastReported) {
                      lastReported = percent;
                      updateData({ progress: percent });
                  }
              },
          });
          if (activeJobRef.current !== queueId) return; // superseded while downloading
          veniceErrorStreakRef.current = 0;

          if (result.status === 'COMPLETED') {
              void refreshVeniceBalance(veniceApiKey, true);
              clearPolling();
              activeJobRef.current = null; // a tick from a leaked interval must not download again
              try {
                  // Venice's copy is left in place (/video/complete is optional): it is the
                  // recovery path when a reload or a lost save interrupts the cloud upload.
                  await persistVideoBlob(result.blob, queueId);
              } catch (persistError: any) {
                  activeGenerationRef.current = false;
                  updateData({ generationStatus: 'error', errorMessage: persistError?.message || 'Failed to save the video' });
                  setIsGenerating(false);
                  return;
              }
              return;
          }

          const progress = veniceProgressPercent(result);
          if (progress !== (dataRef.current.progress || 0)) updateData({ progress });
      } catch (e: any) {
          // Any 4xx other than timeout/rate-limit is a permanent answer (400 bad request,
          // 401/402 auth or balance, 403 region block, 404 unknown job, 422 provider
          // content policy). 5xx and network errors get a couple of retries.
          const fatal =
              e instanceof VeniceError &&
              ((e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) ||
                  e.code === 'generation_failed' || e.code === 'no_download_url' || e.code === 'bad_response' || e.code === 'download_blocked');
          veniceErrorStreakRef.current += 1;
          if (!fatal && veniceErrorStreakRef.current < 3) {
              console.warn('[Venice] Poll failed, will retry:', e?.message);
              return;
          }
          clearPolling();
          activeGenerationRef.current = false;
          const message = e?.name === 'TimeoutError'
              ? 'Downloading the finished video from Venice timed out. Check your connection and try again.'
              : e instanceof TypeError
                  ? 'Lost contact with Venice while waiting for the video. Check your connection, and whether a browser extension or network filter blocks api.venice.ai.'
                  : (e?.message || 'Venice polling failed');
          updateData({ generationStatus: 'error', errorMessage: message });
          setIsGenerating(false);
      } finally {
          if (veniceInFlightRef.current === queueId) veniceInFlightRef.current = null;
      }
  }, [veniceApiKey, updateData]);

  useEffect(() => {
      pollVeniceStatusRef.current = pollVeniceStatus;
  }, [pollVeniceStatus]);

  const startVeniceGeneration = async (consents?: { seedance: VeniceSeedanceConsents }, token = runTokenRef.current) => {
      if (!veniceApiKey) throw new Error('Venice API key missing');
      const veniceModel = getVeniceVideoModelId(modelDef, generationMode);
      const isI2V = generationMode === 'image-to-video';
      const isR2V = generationMode === 'reference-to-video';

      const limit = modelDef.promptCharacterLimit;
      if (limit && prompt.length > limit) {
          throw new Error(`Prompt is too long for ${modelDef.label} (${prompt.length}/${limit} characters).`);
      }
      if (limit && negativePrompt.length > limit) {
          throw new Error(`Negative prompt is too long for ${modelDef.label} (${negativePrompt.length}/${limit} characters).`);
      }
      const clipsInUse = isR2V && videoInputSupported ? inputVideos : [];
      if (isI2V && inputImages.length === 0) throw new Error('Connect an image node to use as the first frame.');
      if (isR2V && inputImages.length === 0 && clipsInUse.length === 0) throw new Error('Connect image or video nodes as references.');

      const toDataUrl = async (url: string, label: string) => {
          const { data: b64, mimeType } = await getBase64FromUrl(url, label);
          return `data:${mimeType};base64,${b64}`;
      };

      let imageUrl: string | undefined;
      let referenceImageUrls: string[] | undefined;
      if (isI2V) {
          imageUrl = await toDataUrl(inputImages[0].url, 'the first frame');
          await assertSeedanceReferenceImage(imageUrl, 'First frame');
      }
      if (isR2V) {
          if (klingProblems.length > 0) throw new Error(klingProblems[0]);
          const refs = inputImages.slice(0, modelDef.maxReferenceImages || 9);
          referenceImageUrls = [];
          for (let i = 0; i < refs.length; i++) {
              const url = await toDataUrl(refs[i].url, refs[i].label || `Reference ${i + 1}`);
              await assertSeedanceReferenceImage(url, refs[i].label || `Reference ${i + 1}`);
              referenceImageUrls.push(url);
          }
      }

      // Connected videos (reference mode): the selected part of each is trimmed in the
      // browser and sent as a reference clip. With the extend hint the new video picks
      // up where the selection ends.
      let referenceVideoUrls: string[] | undefined;
      let omniReferenceTaskType: 'extend' | undefined;
      let referenceVideoTotalSec = 0;
      if (clipsInUse.length > 0) {
          prepAbortRef.current?.abort();
          const prep = new AbortController();
          prepAbortRef.current = prep;
          const count = clipsInUse.length;
          const megabytes = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);
          const selections: { label: string; startSec: number; endSec: number; key: string; load: () => Promise<Blob> }[] = [];
          for (let i = 0; i < count; i++) {
              const clip = clipsInUse[i];
              const label = clip.label || `Clip ${i + 1}`;
              setPrepMessage(`Reading clip ${i + 1} of ${count}`);
              let bytes: Promise<Blob> | undefined;
              // A clip that is not on this device is downloaded; say how far that has got
              const load = () => (bytes ??= loadMediaBlob(clip.url, {
                  userId, ownerUserId, workspaceName, label, defaultMimeType: 'video/mp4',
                  onDownloadProgress: (received, total) => {
                      if (token === runTokenRef.current) setPrepMessage(`Downloading clip ${i + 1} of ${count} (${megabytes(received)}${total ? ` of ${megabytes(total)}` : ''} MB)`);
                  },
              }).then(r => r.blob));
              let range = clipRanges[clip.sourceId];
              if (!range) {
                  const known = clipDurations[clip.sourceId] ?? (await probeVideoDuration(await load(), prep.signal));
                  range = defaultClipRange(known, referenceLimits);
              }
              selections.push({ label, startSec: range.startSec, endSec: range.endSec, key: `${clip.sourceId}|${clip.url}`, load });
          }
          const clipIssues = checkReferenceClips(selections, referenceLimits);
          if (clipIssues.length > 0) throw new Error(clipIssues[0]);
          // Clips cannot be shrunk once recorded the way pictures can: record them at a rate that
          // leaves room in the request for the audio (as sent) and the pictures. A 30 s selection
          // gets about 4.5 Mbps; 15 s or less keeps the usual quality.
          const selectedSec = selections.reduce((sum, s) => sum + (s.endSec - s.startSec), 0);
          const audioSent = isR2V && audioInputSupported
              ? (audioRole === 'music' ? inputAudio.slice(0, 1) : inputAudio).reduce((sum, a) => sum + ((a.sizeBytes ?? 0) * 4) / 3, 0)
              : 0;
          const maxBitsPerSecond = clipBitrateBudget(selectedSec, audioSent);
          referenceVideoUrls = [];
          for (let i = 0; i < selections.length; i++) {
              const s = selections[i];
              const trimmed = await getTrimmedClip(s.key, s.load, s.startSec, s.endSec, ({ step, fraction }) => {
                  if (token !== runTokenRef.current) return;
                  const which = `clip ${i + 1} of ${selections.length}`;
                  setPrepMessage(
                      step === 'opening' ? `Opening ${which}`
                          : step === 'seeking' ? `Finding the start of ${which}`
                              : step === 'waiting' ? `Paused: keep this tab in front to finish ${which}`
                                  : `Trimming ${which} (${Math.round(fraction * 100)}%)`,
                  );
              }, prep.signal, maxBitsPerSecond);
              referenceVideoUrls.push(await blobToDataUrl(trimmed.blob));
              referenceVideoTotalSec += trimmed.durationSec;
          }
          if (prepAbortRef.current === prep) prepAbortRef.current = null;
          if (extendFromClip && modelDef.supportsExtendHint) omniReferenceTaskType = 'extend';
          setPrepMessage(null);
      }

      // Connected audio (reference mode only): one background track, or voice/SFX donor clips.
      let audioUrl: string | undefined;
      let referenceAudioUrls: string[] | undefined;
      if (isR2V && audioInputSupported && inputAudio.length > 0) {
          const problems = checkAudioForVenice(audioRole, inputAudio.map(a => ({ label: a.label, durationSec: a.durationSec, sizeBytes: a.sizeBytes })), inputImages.length + clipsInUse.length, referenceLimits);
          if (problems.length > 0) throw new Error(problems[0]);
          const toAudioDataUrl = async (url: string, label: string) => {
              const { data: b64, mimeType } = await getBase64FromUrl(url, label, 'audio/mpeg');
              return `data:${mimeType && mimeType.startsWith('audio/') ? mimeType : 'audio/mpeg'};base64,${b64}`;
          };
          if (audioRole === 'music') {
              audioUrl = await toAudioDataUrl(inputAudio[0].url, inputAudio[0].label);
          } else {
              referenceAudioUrls = [];
              for (const clip of inputAudio.slice(0, referenceLimits.maxAudioClips)) referenceAudioUrls.push(await toAudioDataUrl(clip.url, clip.label));
          }
      }

      // Everything travels in one request, which Venice refuses at 32 MB (the browser only
      // sees "Failed to fetch"). Large pictures are sent as a reference needs them, so the request
      // uploads quickly, and made smaller still to fit around the clips and audio.
      const clipParts = (referenceVideoUrls || []).map((url, i) => ({ label: clipsInUse[i]?.label || `Clip ${i + 1}`, bytes: url.length }));
      const audioParts = audioUrl
          ? [{ label: inputAudio[0]?.label || 'Music', bytes: audioUrl.length }]
          : (referenceAudioUrls || []).map((url, i) => ({ label: inputAudio[i]?.label || `Audio ${i + 1}`, bytes: url.length }));
      const promptPart = { label: 'the prompt', bytes: requestBytes(prompt) + requestBytes(negativePrompt || '') };
      const pictureLabels = [...(imageUrl ? ['the first frame'] : []), ...(referenceImageUrls || []).map((_, i) => inputImages[i]?.label || `Reference ${i + 1}`)];
      const fitted = await fitPicturesToRequest(
          [...(imageUrl ? [imageUrl] : []), ...(referenceImageUrls || [])],
          [...clipParts, ...audioParts, promptPart].reduce((sum, part) => sum + part.bytes, 0),
          undefined,
          () => setPrepMessage('Making large pictures lighter to send'),
          referenceStep(),
      );
      if (imageUrl) imageUrl = fitted.shift();
      if (referenceImageUrls) referenceImageUrls = fitted;
      setPrepMessage(null);
      const tooLarge = oversizedRequest([
          ...[...(imageUrl ? [imageUrl] : []), ...(referenceImageUrls || [])].map((url, i) => ({ label: pictureLabels[i], bytes: url.length })),
          ...clipParts,
          ...audioParts,
          promptPart,
      ]);
      if (tooLarge) throw new Error(tooLarge);
      // Kling O3 takes the pictures as elements and scene pictures, not as one list
      const kling = klingReferenceSlots && referenceImageUrls ? klingReferenceFields(klingReferenceSlots, referenceImageUrls) : null;
      if (kling) referenceImageUrls = undefined;
      // Models without a resolution setting (Kling) get none
      const resolutionParam = modelDef.supportedResolutions ? resolution : undefined;

      // Image-to-video derives the aspect ratio from the frame; Venice rejects it otherwise.
      // With reference clips, "adaptive" lets the output follow the clip's aspect ratio.
      const aspectRatioParam = isI2V ? undefined : (referenceVideoUrls?.length && matchClipAspect ? 'adaptive' : aspectRatio);
      const audioParam = modelDef.supportsAudioToggle ? audioEnabled : undefined;

      // Fresh price estimate with the exact parameters being queued (the debounced
      // footer quote may lag a config change); the API bills duration × resolution × audio.
      let estimate: number | null = null;
      try {
          estimate = await veniceVideoQuote({ apiKey: veniceApiKey, model: veniceModel, duration, resolution: resolutionParam, aspectRatio: aspectRatioParam, audio: audioParam, referenceVideoTotalDuration: referenceVideoTotalSec || undefined });
      } catch {
          estimate = veniceQuote;
      }

      const queued = await veniceQueueVideo({
          apiKey: veniceApiKey,
          model: veniceModel,
          // Seedance reads its references as <Image 1>, <Video 1>, <Audio 1>; Kling as @Element1, @Image1
          prompt: isR2V ? (klingReferenceSlots ? normalizeReferenceTags(prompt) : toSeedanceTags(prompt)) : prompt,
          negativePrompt: negativePrompt || undefined,
          audioUrl,
          referenceAudioUrls,
          duration,
          resolution: resolutionParam,
          aspectRatio: aspectRatioParam,
          audio: audioParam,
          imageUrl,
          referenceImageUrls,
          elements: kling?.elements,
          sceneImageUrls: kling?.sceneImageUrls,
          referenceVideoUrls,
          omniReferenceTaskType,
          consents,
      });

      console.log('[Venice] Job queued:', queued.queueId, 'model:', queued.model);
      void refreshVeniceBalance(veniceApiKey, true); // the job is paid for now
      if (token !== runTokenRef.current) return; // cancelled while the request was in flight

      // Persist everything needed to resume after a reload in one update.
      updateData({
          jobId: queued.queueId,
          generationStatus: 'generating',
          errorMessage: undefined,
          progress: 0,
          cost: estimate ?? 0,
          videoConfig: {
              model: currentModelId,
              aspectRatio,
              resolution,
              duration,
              generationMode,
              negativePrompt,
              audio: audioEnabled,
              veniceModelId: queued.model,
              veniceDownloadUrl: queued.downloadUrl,
              generationOwnerId: userId,
          },
      });

      veniceErrorStreakRef.current = 0;
      veniceInFlightRef.current = null;
      clearPolling();
      const queueId = queued.queueId;
      activeJobRef.current = queueId;
      const modelForPoll = queued.model;
      pollInterval.current = setInterval(() => {
          pollVeniceStatusRef.current?.(queueId, modelForPoll);
      }, 8000);
      setTimeout(() => pollVeniceStatusRef.current?.(queueId, modelForPoll), 4000);
  };

  // The key prompt is only meaningful while that key is missing.
  useEffect(() => {
      if (!showApiKeyPrompt) return;
      const present = missingKeyType === 'venice' ? !!veniceApiKey : missingKeyType === 'openai' ? !!openAiApiKey : false;
      if (present) setShowApiKeyPrompt(false);
  }, [veniceApiKey, openAiApiKey, showApiKeyPrompt, missingKeyType]);

  // A copy that is not driving the generation (a collaborator, or a reload that never took
  // the job over) unlocks the moment the synced data leaves 'generating'.
  useEffect(() => {
      if (data.generationStatus !== 'generating' && !activeGenerationRef.current) setIsGenerating(false);
  }, [data.generationStatus]);

  // Unmount cleanup: kill any active polling when node is removed
  useEffect(() => {
      isMountedRef.current = true; // StrictMode runs this cleanup and effect once more on mount
      return () => {
          isMountedRef.current = false;
          if (pollInterval.current) {
              clearInterval(pollInterval.current);
              pollInterval.current = null;
          }
      };
  }, []);

  // Resume polling on mount if there's a pending Sora job (page reload recovery)
  useEffect(() => {
      // Only run recovery logic — skip if we're actively generating in this session
      if (activeGenerationRef.current) return;
      
      const resumeVeniceModel = modelDef.provider === 'venice' ? data.videoConfig?.veniceModelId : undefined;
      const isResumable = !!data.jobId && (modelDef.provider === 'openai' || !!resumeVeniceModel);

      // Track the interval this specific effect run creates (if any)
      let recoveryInterval: ReturnType<typeof setInterval> | null = null;

      // A completed node whose video reference is missing, or is a blob URL left from an
      // earlier session (blob URLs never survive a reload; ones created while loading are
      // marked), lost its file before the cloud upload landed; the job can still be fetched.
      const staleBlob = !!data.videoUri && data.videoUri.startsWith('blob:') && !hasSessionCopy
          && !isBlobUploading(data.videoUri) && !resolveUploadedBlob(data.videoUri) && !isRehydratedBlob(data.videoUri);
      const lostVideo = data.generationStatus === 'completed' && (!data.videoUri || staleBlob) && isResumable;

      if ((data.generationStatus === 'generating' || lostVideo) && ownsCurrentJob) {
          if (isResumable) {
              const keyMissing = resumeVeniceModel ? !veniceApiKey : !openAiApiKey;
              if (keyMissing) {
                  // Nothing can be fetched without the provider key: ask for it and leave the job resumable.
                  setIsGenerating(false);
                  setMissingKeyType(resumeVeniceModel ? 'venice' : 'openai');
                  setShowApiKeyPrompt(true);
              } else {
                  // A stale reference stays in place until the job's file replaces it.
                  if (lostVideo) updateData({ generationStatus: 'generating', progress: 95 });
                  setShowApiKeyPrompt(false);
                  setIsGenerating(true);
                  const jobId = data.jobId!;
                  activeJobRef.current = jobId;
                  const tick = () => {
                      if (resumeVeniceModel) pollVeniceStatusRef.current?.(jobId, resumeVeniceModel);
                      else pollSoraStatusRef.current?.(jobId);
                  };
                  recoveryInterval = setInterval(tick, 10000);
                  pollInterval.current = recoveryInterval;
                  // Immediate first poll
                  tick();
              }
          } else {
              // Reset stuck state for non-resumable generations (Veo crashed before completing)
              updateData({ generationStatus: 'idle' });
              setIsGenerating(false);
          }
      } else if (data.generationStatus === 'generating') {
          // Another client's job: shown, never driven from here.
          setIsGenerating(false);
      }
      
      return () => {
          // CRITICAL: Only clean up the interval THIS effect run created.
          // Do NOT touch pollInterval.current if it was set by startSoraGeneration,
          // otherwise the cleanup from the initial mount kills active polling.
          if (recoveryInterval) {
              clearInterval(recoveryInterval);
              if (pollInterval.current === recoveryInterval) {
                  pollInterval.current = null;
              }
          }
      };
  // Re-run on mount, when the job changes, or when a provider key arrives (not on every data change)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.jobId, veniceApiKey, openAiApiKey]);


  // OpenAI Logic
  const startSoraGeneration = async (token: number) => {
       if (!openAiApiKey) throw new Error("OpenAI Key missing");
       const formData = new FormData();
       formData.append('model', currentModelId);
       formData.append('prompt', prompt);
       
       let soraSize = '1280x720';
       if (aspectRatio === '16:9') soraSize = resolution === '1080p' ? '1792x1024' : '1280x720';
       else soraSize = resolution === '1080p' ? '1024x1792' : '720x1280';
       
       formData.append('size', soraSize);
       formData.append('seconds', duration);

       if (inputImages.length > 0) {
           // 1. Fetch the raw data first to handle CORS/Remote URLs
           const { data: b64, mimeType } = await getBase64FromUrl(inputImages[0].url, 'the first frame');
           
           // 2. Parse target dimensions
           const [wStr, hStr] = soraSize.split('x');
           const targetW = parseInt(wStr);
           const targetH = parseInt(hStr);

           // 3. Resize and crop to exact dimensions
           const fullDataUrl = `data:${mimeType};base64,${b64}`;
           const resizedBlob = await resizeImageToBlob(fullDataUrl, targetW, targetH);
           
           formData.append('input_reference', resizedBlob, 'ref.png');
       }

       const response = await fetch('https://api.openai.com/v1/videos', {
           method: 'POST',
           headers: { 'Authorization': `Bearer ${openAiApiKey}` },
           body: formData
       });
       
       const json = await response.json();
       if (!response.ok) {
           // Check for specific moderation error
           if (json.error?.code === 'content_policy_violation') {
               throw new Error("Content blocked by safety filters. Please adjust prompt or image.");
           }
           throw new Error(json.error?.message || "OpenAI Error");
       }
       
       console.log('[Sora] Job created:', json.id, 'Starting polling...');
       if (token !== runTokenRef.current) return; // cancelled while the request was in flight
       
       // Save jobId and ensure status is 'generating' (set together to avoid race); the owner
       // is recorded so only this user's clients resume the job after a reload.
       updateData({
           jobId: json.id,
           generationStatus: 'generating',
           progress: 0,
           videoConfig: { ...(dataRef.current.videoConfig ?? { model: currentModelId, aspectRatio, resolution }), generationOwnerId: userId },
       });
       
       // Start polling directly — don't rely on useEffect (avoids race condition)
       if (pollInterval.current) clearInterval(pollInterval.current);
       const jobId = json.id;
       activeJobRef.current = jobId;
       pollInterval.current = setInterval(() => {
           pollSoraStatusRef.current?.(jobId);
       }, 10000);
       // Do an immediate first poll after a brief delay for the job to initialize
       setTimeout(() => pollSoraStatusRef.current?.(jobId), 3000);
  };

  // Google Logic (Veo) - Keeps running in memory (SDK Limitation, hard to serialize operation object)
  const generateVeoVideo = async (token: number) => {
      const keyToUse = apiKey || process.env.API_KEY;
      const ai = new GoogleGenAI({ apiKey: keyToUse });
      let operation;

      const isVeo31 = currentModelId === 'veo-3.1-generate-preview';

      // Build base config
      const baseConfig: any = {
          numberOfVideos: 1,
          resolution: resolution as any,
          aspectRatio: aspectRatio as any,
          durationSeconds: parseInt(duration) || 8,
      };

      // Add negative prompt if provided
      if (negativePrompt.trim()) {
          baseConfig.negativePrompt = negativePrompt;
      }

      switch (generationMode) {
          case 'text-to-video': {
              // Pure text prompt → video
              operation = await ai.models.generateVideos({
                  model: currentModelId,
                  prompt,
                  config: baseConfig,
              });
              break;
          }

          case 'image-to-video': {
              // Single image (first frame) → video
              if (inputImages.length === 0) throw new Error('Connect an image node as the first frame.');
              const { data: imgData, mimeType } = await getBase64FromUrl(inputImages[0].url, 'the first frame');

              operation = await ai.models.generateVideos({
                  model: currentModelId,
                  prompt,
                  image: { imageBytes: imgData, mimeType },
                  config: baseConfig,
              });
              break;
          }

          case 'frames-to-video': {
              // First frame + Last frame → video (interpolation)
              if (inputImages.length < 2) throw new Error('Connect two image nodes: first frame and last frame.');
              const first = await getBase64FromUrl(inputImages[0].url, 'the first frame');
              const last = await getBase64FromUrl(inputImages[1].url, 'the last frame');

              operation = await ai.models.generateVideos({
                  model: currentModelId,
                  prompt,
                  image: { imageBytes: first.data, mimeType: first.mimeType },
                  config: {
                      ...baseConfig,
                      lastFrame: { imageBytes: last.data, mimeType: last.mimeType },
                  },
              });
              break;
          }

          case 'ingredients-to-video': {
              // Multiple reference images as style/subject ingredients (Veo 3.1 only)
              if (!isVeo31) throw new Error('Reference images are only supported with Veo 3.1 High Quality.');
              if (inputImages.length === 0) throw new Error('Connect image nodes as reference assets (up to 3).');

              const refs = await Promise.all(
                  inputImages.slice(0, 3).map(async (img) => {
                      const { data: imgData, mimeType } = await getBase64FromUrl(img.url, img.label || 'a reference image');
                      return {
                          image: { imageBytes: imgData, mimeType },
                          referenceType: VideoGenerationReferenceType.ASSET,
                      };
                  })
              );

              operation = await ai.models.generateVideos({
                  model: currentModelId,
                  prompt,
                  config: {
                      ...baseConfig,
                      // Reference images require 720p and 8s duration
                      resolution: '720p',
                      durationSeconds: 8,
                      referenceImages: refs,
                  },
              });
              break;
          }

          case 'extend-video': {
              // Extend existing Veo-generated video (Veo 3.1 only)
              if (!isVeo31) throw new Error('Video extension is only supported with Veo 3.1 High Quality.');
              if (!data.lastVeoVideoRef) throw new Error('Generate a video first, then switch to Extend mode.');

              operation = await ai.models.generateVideos({
                  model: currentModelId,
                  prompt: prompt || 'Continue the video naturally.',
                  video: data.lastVeoVideoRef,
                  config: {
                      numberOfVideos: 1,
                      resolution: '720p', // Extension only supports 720p
                      durationSeconds: 8,  // Extension requires 8s
                  },
              } as any);
              break;
          }

          default:
              throw new Error(`Unknown generation mode: ${generationMode}`);
      }
      
      // We can't persist the 'operation' object easily in JSON, so Veo remains a session-bound generation for now.
      // Generation status is already 'generating' from top-level call.

      // Poll with timeout (5 minutes max for Veo)
      const veoTimeout = Date.now() + 5 * 60 * 1000;
      while (!operation.done) {
          if (Date.now() > veoTimeout) {
              throw new Error('Video generation timed out after 5 minutes. Please try again.');
          }
          await new Promise(r => setTimeout(r, 10000));
          operation = await ai.operations.getVideosOperation({ operation });
          if (token !== runTokenRef.current) return; // cancelled; the node was already reset
      }
      if (token !== runTokenRef.current) return; // cancelled before the job finished
      if (operation.error) throw new Error((operation.error as any).message);
      
      const vid = (operation.response || (operation as any).result)?.generatedVideos?.[0];
      if (!vid) throw new Error("Safety filter triggered or no video returned");
      
      // Store video reference for potential extension
      const veoVideoRef = vid.video;
      
      if (token !== runTokenRef.current) return; // cancelled; do not download or save the abandoned clip
      const dlLink = vid.video.uri;
      const res = await fetch(`${dlLink}&key=${keyToUse}`);
      const blob = await res.blob();
      let videoUri = URL.createObjectURL(blob);
      
      // Convert blob to base64 for uploads and auto-save
      const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
      });
      
      // Generate shared timestamp for consistent filenames
      const timestamp = Date.now() + Math.floor(Math.random() * 1000);
      const filename = `videos_${timestamp}.mp4`;
      
      // AUTO-SAVE (both local and Google Drive if enabled)
      try {
          if (onAutoSaveFile) {
              await onAutoSaveFile(base64, filename, 'Videos', 'mp4');
          }
      } catch (autoSaveError) {
          console.warn('Video auto-save failed:', autoSaveError);
      }
      
      // UPLOAD TO CLOUD - convert blob to file:// reference (using same timestamp)
      if (userId) {
          onAutoSaveStatus?.('cloud', 'Uploading video to cloud...');
          try {
              const fileRef = await uploadWithDeduplication(userId, base64, {
                  category: 'Videos',
                  workspaceName,
                  displayNamePrefix: 'videos', // Match local filename format
                  extension: 'mp4',
                  nodeId,
                  timestamp, // Use same timestamp as local save
              });
              // The bytes stay in memory for this session, so the editor need not download them again
              rememberSessionBlob(fileRef, videoUri);
              videoUri = fileRef;
              // Register mapping for offline resolution
              if (fileRef.startsWith('file://')) {
                  const localPath = `Videos/videos_${timestamp}.mp4`;
                  registerLocalFileMapping(fileRef, localPath);
              }
              onAutoSaveStatus?.('cloud', 'Uploaded video to cloud');
          } catch (uploadError) {
              console.warn('Immediate video upload failed, keeping blob:', uploadError);
          }
      }
      
      if (token !== runTokenRef.current) return; // cancelled or replaced while saving; leave the node to the newer run
      activeGenerationRef.current = false;
      updateData({ 
          videoUri, 
          generationStatus: 'completed', 
          timestamp: Date.now(), 
          cost: modelDef.baseCost,
          lastVeoVideoRef: veoVideoRef, // Store for extend-video
      });
      setIsGenerating(false);
  };

  const runVideoGeneration = async (consents?: { seedance: VeniceSeedanceConsents }) => {
      if (isViewOnly) return;
      if (!prompt.trim() && generationMode !== 'extend-video') return;
      if (!(await checkApiKey())) return;

      // A previous job whose upload is still running keeps its resumable 'generating' state
      // until a new job replaces it; a failed start must not demote it.
      const pendingJobId = dataRef.current.generationStatus === 'generating' && hasSessionCopy ? dataRef.current.jobId : undefined;

      // IMMEDIATE FEEDBACK: Set state before async operations
      activeGenerationRef.current = true;
      setIsGenerating(true);
      setStartError(null);
      updateData(pendingJobId ? { errorMessage: undefined } : { generationStatus: 'generating', errorMessage: undefined, progress: 0 });

      const token = ++runTokenRef.current; // a Cancel during the start request invalidates this run
      try {
          if (modelDef.provider === 'openai') {
              await startSoraGeneration(token);
          } else if (modelDef.provider === 'venice') {
              await startVeniceGeneration(consents, token);
          } else {
              await generateVeoVideo(token);
          }
      } catch (e: any) {
          setPrepMessage(null);
          if (token !== runTokenRef.current) return; // cancelled meanwhile; the node was already reset
          activeGenerationRef.current = false;
          setIsGenerating(false);
          const previousJobIntact = !!pendingJobId && dataRef.current.jobId === pendingJobId;
          // Venice can ask for a one-time consent attestation for Seedance media.
          if (e instanceof VeniceError && e.code === 'needs_consent') {
              if (!previousJobIntact) updateData({ generationStatus: 'idle', errorMessage: undefined });
              const docsUrl = (e.details as any)?.docs_url;
              setVeniceConsent({ message: e.message, docsUrl: typeof docsUrl === 'string' ? docsUrl : undefined });
              return;
          }
          if (previousJobIntact) setStartError(e.message || 'Error'); // the pending job keeps its resumable state
          else updateData({ generationStatus: 'error', errorMessage: e.message || 'Error' });
      }
  };

  // Stop following the current job. The provider may still finish it, but this node lets go.
  const cancelTracking = () => {
      runTokenRef.current += 1; // a start request still in flight must not queue a job
      prepAbortRef.current?.abort(); // and a clip being prepared for it stops
      prepAbortRef.current = null;
      clearPolling();
      activeJobRef.current = null;
      activeGenerationRef.current = false;
      setIsGenerating(false);
      setShowApiKeyPrompt(false);
      setPrepMessage(null);
      updateData({ generationStatus: 'idle', jobId: undefined, progress: 0, errorMessage: undefined });
  };

  const generateVideo = () => runVideoGeneration();

  // The input tiles for the chosen mode: frames hold one picture each; reference slots count what they hold
  const firstImage = inputImages[0];
  const secondImage = inputImages[1];
  const frameTile = (label: string, img: typeof inputImages[number] | undefined, needed: boolean) => (
    <InputTile
      key={label}
      icon={<ImagePlus size={16} />}
      label={label}
      needed={needed && !img}
      filled={img ? <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" /> : undefined}
      onRemove={img && onRemoveConnection ? () => onRemoveConnection(img.id) : undefined}
      onClick={openPicker ? (anchor) => openPicker(anchor, 'image') : undefined}
      disabled={busy}
      title={img ? `${label}: ${img.label}` : `${label}: connect an image node or choose a reference`}
    />
  );
  const inputTiles: React.ReactNode[] = [];
  if (generationMode === 'image-to-video') inputTiles.push(frameTile('Start frame', firstImage, true));
  if (generationMode === 'frames-to-video') inputTiles.push(frameTile('First frame', firstImage, true), frameTile('Last frame', secondImage, true));
  if (generationMode === 'ingredients-to-video' || generationMode === 'reference-to-video') {
    const nothingYet = inputImages.length === 0 && (!videoInputSupported || inputVideos.length === 0);
    inputTiles.push(
      <InputTile key="images" icon={<ImageIcon size={16} />} label="Image refs" count={inputImages.length} needed={nothingYet}
        onClick={openPicker ? (anchor) => openPicker(anchor, 'image') : undefined} disabled={busy}
        title={`Pictures: ${inputImages.length} attached. Connect image nodes or choose references.`} />,
    );
    if (generationMode === 'reference-to-video' && videoInputSupported) {
      inputTiles.push(
        <InputTile key="videos" icon={<Film size={16} />} label="Video refs" count={inputVideos.length}
          onClick={openPicker ? (anchor) => openPicker(anchor, 'video') : undefined} disabled={busy}
          title={`Clips: ${inputVideos.length} attached, up to ${referenceLimits.clipsTotalMaxSec}s in total.`} />,
      );
    }
    if (generationMode === 'reference-to-video' && audioInputSupported) {
      inputTiles.push(
        <InputTile key="audio" icon={<AudioLines size={16} />} label="Audio refs" count={inputAudio.length}
          onClick={openPicker ? (anchor) => openPicker(anchor, 'audio') : undefined} disabled={busy}
          title={`Audio: ${inputAudio.length} attached.`} />,
      );
    }
  }
  // What is still missing, in a word
  const inputHint =
    generationMode === 'image-to-video' && inputImages.length === 0 ? 'Connect an image node or choose a reference as the start frame.'
      : generationMode === 'frames-to-video' && inputImages.length < 2 ? `Connect two image nodes: the first and the last frame.${inputImages.length === 1 ? ' One is attached.' : ''}`
      : generationMode === 'ingredients-to-video' && inputImages.length === 0 ? 'Add up to 3 pictures of characters, props or places.'
      : generationMode === 'reference-to-video' && inputImages.length === 0 && inputVideos.length === 0
        ? (modelDef.referenceElements
          ? 'Add Reference nodes: up to 4 characters or props (@Element1…) and 4 backgrounds or styles (@Image1…). Their Reference tag decides which.'
          : `Add pictures${videoInputSupported ? ', clips' : ''}${audioInputSupported ? ' or audio' : ''} as references. Pictures need at least 300px on the short side.`)
      : null;
  const showReferenceStrip = inputImages.length > 0 && (generationMode === 'reference-to-video' || generationMode === 'ingredients-to-video');

  const modelsList = useMemo(() => getVideoModels(), []);
  const modelOptions = useMemo<ChipOption<string>[]>(
    () => modelsList.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: PROVIDER_NAMES[m.provider] ?? m.provider })),
    [modelsList],
  );

  // What the stage shows: the wait for a job, a video, or nothing yet
  const stageWaits = status === 'generating' && !hasSessionCopy;
  const stageHasVideo = !stageWaits && !!(data.videoUri || hasSessionCopy);

  return (
    <div ref={fitRef} className="w-full h-full flex flex-col bg-white overflow-hidden">
      {/* PREVIEW: 16:9 of the node's width, whatever the options below. A video plays on ink;
          with none to show, the stage is the light well of the Image node. */}
      <div className={`relative shrink-0 w-full aspect-video flex items-center justify-center overflow-hidden ${stageHasVideo ? 'bg-ink' : 'bg-cream-50 border-b border-cream-200'}`} data-node-preview data-stage={stageHasVideo ? 'video' : stageWaits ? 'waiting' : 'empty'}>
          {stageWaits ? (
              <div className="flex flex-col items-center gap-2 p-4 text-center">
                  {/* ui-ok: the loading orb is not an icon; it has the size it has in the Image node */}
                  <GlassOrb size={56} />
                  <span className="mt-1 text-xs font-medium text-ink-soft">
                      {prepMessage ?? (modelDef.provider === 'openai' || isVenice ? `Generating... ${progressPercent}%` : 'Generating...')}
                  </span>
                  {(modelDef.provider === 'openai' || isVenice) && (
                      <span className="ns-t-meta">You can leave this page. It will resume.</span>
                  )}
                  {mayControlJob && (
                      <button type="button" onClick={cancelTracking} className="ns-btn ns-btn-ghost ns-btn-xs mt-1">
                          Cancel
                      </button>
                  )}
              </div>
          ) : stageHasVideo ? (
              <div className="relative w-full h-full group">
                  {isResolvingVideo && !hasSessionCopy ? (
                      <div className="h-full flex flex-col items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-[color:var(--tone)]" size={24} />
                          <span className="text-xs font-medium text-cream/60">Loading video...</span>
                      </div>
                  ) : (
                      <>
                          <VideoPlayer src={videoSrc} className="w-full h-full" label="Generated video" />
                          {status === 'generating' && (
                              <div className="ns-glass-dark absolute top-2 left-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-medium">
                                  <Loader2 className="animate-spin" size={12} /> Saving to cloud
                              </div>
                          )}
                          {/* Share and save: one dark glass bar over the video, as over the picture in the Image node (at the top here; the player's own controls are at the bottom) */}
                          <div className="node-reveal ns-glass-dark absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 px-1 py-1 rounded-full">
                              <button
                                  onClick={() => setShowTwitterModal(true)}
                                  className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-dark"
                                  title="Share to Twitter"
                                  aria-label="Share to Twitter"
                              >
                                  <Twitter />
                              </button>
                              <a href={videoSrc} download={`video-${Date.now()}.mp4`} className="ns-btn ns-btn-ghost-dark ns-btn-xs h-6">
                                  <Download size={14} /> Save MP4
                              </a>
                          </div>
                      </>
                  )}
              </div>
          ) : (
              <div className="ns-empty select-none" data-stage-empty>
                  <span className="ns-mark ns-mark-lg"><Video /></span>
                  <p className="ns-empty-text">No video yet</p>
              </div>
          )}
          
          {/* Error Message */}
          {((status === 'error' && data.errorMessage) || startError) && (
              <div className="absolute inset-0 z-10 bg-white/95 flex flex-col items-center justify-center p-4 text-center">
                   <span className="ns-mark ns-mark-sm mb-2" data-tone="danger"><AlertCircle /></span>
                   <p className="max-w-full text-xs font-medium text-danger-deep break-words line-clamp-6" title={startError || data.errorMessage}>{startError || data.errorMessage}</p>
                   <button
                       onClick={() => {
                           setStartError(null);
                           if (status === 'error') updateData({ generationStatus: 'idle', errorMessage: undefined });
                       }}
                       className="ns-btn ns-btn-ghost ns-btn-xs mt-3"
                   >
                       Dismiss
                   </button>
              </div>
          )}
          
          {/* API Key Modal */}
          {showApiKeyPrompt && (
              <div className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center p-4 text-center">
                  <span className="ns-mark ns-mark-sm mb-2" data-tone="warn"><AlertCircle /></span>
                  <p className="ns-t-title mb-2">
                      {missingKeyType === 'venice' ? 'Venice AI' : missingKeyType === 'openai' ? 'OpenAI' : 'Google'} key required
                  </p>
                  <div className="flex gap-2">
                      <button onClick={handleSelectKey} className="ns-btn ns-btn-primary ns-btn-xs">Set key</button>
                      {status === 'generating' && mayControlJob && (
                          <button type="button" onClick={cancelTracking} className="ns-btn ns-btn-ghost ns-btn-xs">Cancel</button>
                      )}
                  </div>
              </div>
          )}

          {/* Seedance consent attestation (only when Venice asks for it) */}
          {veniceConsent && (
              <div className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center p-3 text-center">
                  <span className="ns-mark ns-mark-sm mb-1.5" data-tone="warn"><AlertCircle /></span>
                  <p className="ns-t-title mb-1">Seedance needs your confirmation</p>
                  <p className="ns-t-small mb-1.5 max-h-12 overflow-y-auto whitespace-pre-wrap">{veniceConsent.message}</p>
                  <p className="ns-t-meta mb-2">
                      By continuing you accept the provider terms, confirm you have the legal right to use the submitted media, and acknowledge it may be screened.
                      {veniceConsent.docsUrl && (
                          <> <a href={veniceConsent.docsUrl} target="_blank" rel="noopener noreferrer" className="ns-link">Read the policy</a></>
                      )}
                  </p>
                  <div className="flex gap-2">
                      <button onClick={() => setVeniceConsent(null)} className="ns-btn ns-btn-ghost ns-btn-xs">Cancel</button>
                      <button
                          onClick={() => { setVeniceConsent(null); runVideoGeneration(SEEDANCE_CONSENTS); }}
                          className="ns-btn ns-btn-primary ns-btn-xs"
                      >
                          I confirm, generate
                      </button>
                  </div>
              </div>
          )}
      </div>

      {/* CONTROLS keep their own height; the prompt field takes the room left, so a model with
          more or fewer options changes only the field. When even the smallest field would not
          fit, the node grows (useFitNodeHeight). */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex flex-col gap-2.5 px-3 pt-3" data-node-controls>
          {/* Mode */}
          {(modelDef.provider === 'google' || isVenice) && availableModes.length > 1 && (
            <ModeSwitch
              label="Mode"
              value={generationMode as string}
              onChange={(mode) => updateConfig('generationMode', mode)}
              disabled={busy}
              options={availableModes.map((mode: string) => ({ value: mode, label: MODE_INFO[mode]?.label ?? mode, icon: MODE_INFO[mode]?.icon }))}
            />
          )}

          {/* Inputs: one tile per slot the mode takes; a click chooses from the workspace's references */}
          {inputTiles.length > 0 && <div className="flex flex-wrap gap-2" data-input-tiles>{inputTiles}</div>}
          {inputHint && <p className="ns-t-meta -mt-1" data-input-hint>{inputHint}</p>}

          {/* Pictures used as references: each with the tag the prompt names it by. A tag is
              what the user types, so it is never cut: the tile's column is as wide as its tag
              needs (a file name, where there are no tags, is cut at the tile's width). */}
          {showReferenceStrip && (
             <div>
                 <div className="flex flex-wrap gap-x-2 gap-y-2.5" data-reference-strip>
                     {inputImages.slice(0, generationMode === 'ingredients-to-video' ? 3 : maxRefs).map((img, i) => (
                         <div key={`${img.id}-${i}`} className="flex flex-col items-center min-w-[60px] group/tile">
                             <div className="relative">
                                 <div
                                     className={`${REF_THUMB} ${mentionsEnabled ? 'cursor-pointer hover:ring-lavender' : ''}`}
                                     role={mentionsEnabled ? 'button' : undefined}
                                     tabIndex={mentionsEnabled ? 0 : undefined}
                                     title={mentionsEnabled ? `Insert ${tagOf(i)} into the prompt` : img.label}
                                     onClick={mentionsEnabled ? () => insertTagFromStrip(i) : undefined}
                                     onKeyDown={mentionsEnabled ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); insertTagFromStrip(i); } } : undefined}
                                 >
                                     <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" />
                                 </div>
                                 <TileRemoveButton label="Remove reference" onClick={(e) => { e.stopPropagation(); onRemoveConnection && onRemoveConnection(img.id); }} disabled={busy} />
                             </div>
                             <span
                                 className={`mt-1 text-2xs leading-tight text-center ${mentionsEnabled ? 'whitespace-nowrap text-ink font-semibold' : 'w-[60px] truncate text-ink-soft'}`}
                                 title={img.label}
                                 data-mention-tag={mentionsEnabled ? tagOf(i) : undefined}
                             >
                                 {mentionsEnabled ? tagOf(i) : img.label || `Ref ${i + 1}`}
                             </span>
                         </div>
                     ))}
                 </div>
                 {mentionsEnabled && (
                   <p className="ns-t-meta mt-1.5">
                     {klingReferenceSlots
                       ? 'Characters, props and untagged pictures are @Element1…, backgrounds and styles @Image1…, from their Reference tags. Type @ in the prompt or click a picture to insert one.'
                       : <>Name them in the prompt as {mentionRefs.slice(0, 3).map(ref => ref.tag).join(', ')}{mentionRefs.length > 3 ? '…' : ''}. Type @ or click a picture or tag.</>}
                   </p>
                 )}
                 {klingProblems.map(problem => (
                   <Problem key={problem} className="mt-1.5">{problem}</Problem>
                 ))}
             </div>
          )}

          {/* Google text-to-video: connected pictures shown for reference */}
          {inputImages.length > 0 && (generationMode === 'text-to-video' || modelDef.provider !== 'google') && modelDef.provider !== 'openai' && !isVenice && (
             <div className="flex gap-2">
                 {inputImages.slice(0, modelDef.maxReferenceImages || 1).map((img, i) => (
                     <div key={`${img.id}-${i}`} className="relative group/tile">
                         <div className={REF_THUMB}>
                             <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" />
                         </div>
                         <TileRemoveButton label="Remove reference" onClick={() => onRemoveConnection && onRemoveConnection(img.id)} />
                     </div>
                 ))}
             </div>
          )}

          {/* Veo: extend the last generation */}
          {generationMode === 'extend-video' && (
            <div className="ns-note" data-tone={data.lastVeoVideoRef ? 'ok' : 'warn'}>
              <Video aria-hidden="true" />
              <span>{data.lastVeoVideoRef ? 'The previous generation is ready to extend (+7s).' : 'Generate a video first, then switch to Extend.'}</span>
            </div>
          )}

          {isVenice && generationMode === 'text-to-video' && inputImages.length > 0 && (
            <p className="ns-t-meta">
              {availableModes.length > 1
                ? `Connected images are not used in Text mode. Switch to ${availableModes.includes('reference-to-video') ? 'Image or Reference' : 'Image'} to use them.`
                : `${modelDef.label} makes video from text only; connected images are not used.`}
            </p>
          )}

          {/* Audio */}
          {inputAudio.length > 0 && (
            <div>
              {audioInputSupported ? (
                <>
                  <div className="flex flex-col gap-1">
                    {inputAudio.map((clip) => (
                      <div key={clip.id} className="flex items-center gap-2 h-8 pl-2.5 pr-1 rounded-lg bg-cream-100 ring-1 ring-cream-300 text-2xs text-ink-soft">
                        <Music size={12} className="text-[color:var(--tone-deep)] shrink-0" aria-hidden="true" />
                        <span className="truncate flex-1 text-ink" title={clip.label}>{clip.label}</span>
                        {refTag('audio', clip.url) && (
                          <button type="button" onClick={() => insertTag(refTag('audio', clip.url)!)} disabled={busy} className={`no-drag px-1.5 ${REF_TAG} ${REF_TAG_IDLE}`} title={`Insert ${refTag('audio', clip.url)} into the prompt`} data-ref-tag>
                            {refTag('audio', clip.url)}
                          </button>
                        )}
                        {clip.durationSec !== undefined && <span className="text-ink-faint shrink-0 tabular-nums">{formatDuration(clip.durationSec)}</span>}
                        <button type="button" onClick={() => onRemoveConnection && onRemoveConnection(clip.id)} className="no-drag ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" aria-label={`Disconnect ${clip.label}`} title={`Disconnect ${clip.label}`} disabled={busy}>
                          <X />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="ns-t-meta">Use as</span>
                    <SettingChip
                      icon={<AudioLines size={14} />}
                      label="Use the audio as"
                      value={audioRole}
                      options={[
                        { value: 'music', label: 'Background music', hint: 'one file, up to 30s' },
                        { value: 'reference', label: 'Voice or sound reference', hint: `2–${referenceLimits.audioClipMaxSec}s clips` },
                      ]}
                      onChange={(role) => updateConfig('audioRole', role)}
                      disabled={busy}
                    />
                  </div>
                  {audioProblems.map(p => <Problem key={p} className="mt-1.5">{p}</Problem>)}
                </>
              ) : (
                <p className="ns-t-meta">
                  {isVenice
                    ? 'Audio is only used by Seedance 2.x in Reference mode; the connected audio is not used here.'
                    : 'This model does not take audio; the connected audio is not used.'}
                </p>
              )}
            </div>
          )}

          {/* Clips: what part of each is sent */}
          {inputVideos.length > 0 && (
            <div>
              {videoInputSupported ? (
                <>
                  <div className="flex flex-col gap-1">
                    {inputVideos.map((clip, i) => (
                      <ReferenceClipEditor
                        key={clip.id}
                        clip={{ ...clip, label: clip.label || `Clip ${i + 1}` }}
                        range={clipRanges[clip.sourceId]}
                        durationSec={clipDurations[clip.sourceId]}
                        continues={extendFromClip}
                        limits={referenceLimits}
                        tag={refTag('video', clip.url)}
                        onInsertTag={insertTag}
                        onRangeChange={(sourceId, range) => patchVideoConfig({ referenceClips: { ...clipRanges, [sourceId]: range } })}
                        onDurationKnown={(sourceId, seconds) => setClipDurations(prev => (prev[sourceId] === seconds ? prev : { ...prev, [sourceId]: seconds }))}
                        onDisconnect={onRemoveConnection}
                        userId={userId}
                        workspaceName={workspaceName}
                        ownerUserId={ownerUserId}
                        disabled={busy}
                      />
                    ))}
                  </div>
                  {/* On or off: the design system's switch */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-xs font-medium text-ink-soft">
                    {modelDef.supportsExtendHint && (
                      <label className={`flex items-center gap-2 ${busy ? '' : 'cursor-pointer'}`}>
                        <input type="checkbox" className="no-drag ns-switch" checked={extendFromClip} onChange={e => patchVideoConfig({ extendFromClip: e.target.checked })} disabled={busy} />
                        Continue the clip (extend)
                      </label>
                    )}
                    <label className={`flex items-center gap-2 ${busy ? '' : 'cursor-pointer'}`}>
                      <input type="checkbox" className="no-drag ns-switch" checked={matchClipAspect} onChange={e => patchVideoConfig({ matchClipAspect: e.target.checked })} disabled={busy} />
                      Match the clip's aspect ratio
                    </label>
                  </div>
                  <p className="ns-t-meta mt-1.5">
                    {modelDef.supportsExtendHint
                      ? (extendFromClip
                          ? 'The new video picks up where the selection ends. Describe what happens next.'
                          : 'The selection guides motion, camera and style. Describe the edit or the new scene.')
                      : 'Venice reads from the prompt whether to continue or restyle the clip; say which you want.'}
                  </p>
                  {clipProblems.map(p => <Problem key={p} className="mt-1.5">{p}</Problem>)}
                </>
              ) : (
                <p className="ns-t-meta">
                  {isVenice
                    ? 'Reference clips are only used by Seedance 2.x in Reference mode; the connected video is not used here.'
                    : 'This model does not take video; the connected video is not used.'}
                </p>
              )}
            </div>
          )}

          {/* Prompt: it takes the room that is left */}
          <div className="relative flex-1 flex flex-col min-h-[88px]" data-prompt-box>
              <textarea
                 ref={promptRef}
                 className={`${promptFieldClass} relative z-[1]`}
                 placeholder={
                   generationMode === 'image-to-video' ? 'Describe how to animate this image...' :
                   generationMode === 'frames-to-video' ? 'Describe the transition between frames...' :
                   generationMode === 'ingredients-to-video' ? 'Describe a scene using the reference images...' :
                   generationMode === 'reference-to-video' ? (
                     !mentionsEnabled ? 'Describe the scene; the references guide subjects and style...'
                       : extendFromClip && !klingReferenceSlots && inputVideos.length > 0 ? 'Extend @Video1, then describe what happens next. Type @ to tag a reference...'
                       : inputImages.length > 0 ? 'Describe the scene. Type @ to tag a reference, e.g. "the girl of @Image1"...'
                       : 'Describe the scene. Type @ to tag a reference, e.g. "the camera move of @Video1"...'
                   ) :
                   generationMode === 'extend-video' ? 'Describe how the video should continue... (optional)' :
                   'Describe the video you want to create...'
                 }
                 value={prompt}
                 onChange={e => { markPromptEdit(); updateData({ prompt: e.target.value }); tags.onFieldChange(e.target); }}
                 {...tags.fieldProps}
                 disabled={busy}
                 aria-label="Prompt"
              />
              <PromptTagLayer tags={tags} />
          </div>

          {/* Negative prompt (folded away) */}
          {(modelDef.provider === 'google' || isVenice) && (
            <details className="-mt-1 pb-1 group/neg">
              <summary className="w-fit flex items-center gap-1 text-2xs font-semibold text-ink-faint cursor-pointer hover:text-ink select-none list-none">
                <ChevronRight size={12} className="transition-transform group-open/neg:rotate-90" aria-hidden="true" />Negative prompt{negativePrompt ? ' (set)' : ''}
              </summary>
              <textarea
                className="ns-field ns-field-sm h-12 mt-1"
                placeholder="What to avoid, e.g. blurry, low quality, text..."
                value={negativePrompt}
                onChange={e => updateConfig('negativePrompt', e.target.value)}
                disabled={busy}
              />
            </details>
          )}
        </div>

          <PromptTagPicker tags={tags} />
          {promptOverLimit > 0 && (
            <Problem className="shrink-0 mx-3 mt-1.5 mb-2" data-prompt-limit>
              The prompt is {prompt.length.toLocaleString('en-US')} characters; {modelDef.label} takes at most {modelDef.promptCharacterLimit!.toLocaleString('en-US')}. Shorten it by {promptOverLimit.toLocaleString('en-US')}.
            </Problem>
          )}
          {mentionsEnabled && unknownTags.length > 0 && (
            <Problem className="shrink-0 mx-3 mt-1.5 mb-2">
              {unknownTags.join(', ')} {unknownTags.length === 1 ? 'names' : 'name'} no attached reference. Attached: {mentionRefs.map(ref => ref.tag).join(', ')}.
            </Problem>
          )}

          {/* Settings as chips, then the price, the balance and the send button */}
          <SettingsRow>
              <SettingChip
                  icon={<Film size={14} />}
                  label="Model"
                  value={currentModelId}
                  display={shortModelLabel(modelDef.label)}
                  options={modelOptions}
                  onChange={(id) => updateConfig('model', id)}
                  disabled={busy || status === 'generating'}
              />
              {modelDef.supportedRatios && !(isVenice && generationMode === 'image-to-video') && (
                  <SettingChip icon={<RectangleHorizontal size={14} />} label="Aspect ratio" value={aspectRatio} display={aspectRatio} options={modelDef.supportedRatios} onChange={(v) => updateConfig('aspectRatio', v)} disabled={busy} />
              )}
              {modelDef.supportedResolutions && (
                  <SettingChip icon={<Scan size={14} />} label="Resolution" value={resolution} options={modelDef.supportedResolutions} onChange={(v) => updateConfig('resolution', v)} disabled={busy} />
              )}
              {modelDef.supportedDurations && (
                  <SettingChip icon={<Clock3 size={14} />} label="Duration" value={duration} options={modelDef.supportedDurations} onChange={(v) => updateConfig('duration', v)} disabled={busy} />
              )}
              {modelDef.supportsAudioToggle && (
                  <SettingChip
                      icon={audioEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                      label="Sound"
                      display={audioEnabled ? 'On' : 'Off'}
                      pressed={audioEnabled}
                      onChange={() => updateConfig('audio', !audioEnabled)}
                      disabled={busy}
                  />
              )}
              <SendGroup
                  label={generationMode === 'extend-video' ? 'Extend' : 'Generate'}
                  onSend={generateVideo}
                  busy={busy}
                  disabled={!prompt.trim() && generationMode !== 'extend-video'}
                  price={
                    <>
                      {isVenice
                        ? (veniceQuote !== null ? `$${veniceQuote.toFixed(2)}` : veniceQuoteLoading ? 'quoting…' : veniceApiKey ? 'no quote' : 'add Venice key')
                        : `$${modelDef.baseCost.toFixed(2)}`}
                      {isVenice && veniceApiKey && <> · <VeniceBalanceLabel apiKey={veniceApiKey} estimateUsd={veniceQuote} /></>}
                    </>
                  }
              />
          </SettingsRow>
      </div>
      {refPicker.picker}

      {/* Twitter Share Modal */}
      {videoSrc && (
        <ShareToTwitterModal
          isOpen={showTwitterModal}
          onClose={() => setShowTwitterModal(false)}
          mediaUrl={videoSrc}
          mediaType="video"
          defaultCaption={prompt ? `Created with AI: ${prompt.slice(0, 200)}${prompt.length > 200 ? '...' : ''}` : ''}
        />
      )}
    </div>
  );
};